import { useEffect, useState } from 'react'
import { View } from 'react-native'
import Button from '@ca/ui/buttons/Button'
import ModalWide from '@ca/ui/components/Modal/variants/ModalWide'
import Typography from '@ca/ui/typography'
import { copyText } from './copyText'
import {
  criticalShCaptureLogR,
  formatShCaptureLogConfig,
  formatShCaptureLogR,
  formatShCaptureLogReport,
  readShCaptureLogRatio,
  selectShCaptureLogEntries,
  summariseShCaptureLog,
  type ShCaptureLogConfig,
  type ShCaptureLogEntry,
  type ShCaptureLogSide,
} from './shCaptureLog'

/**
 * Dev-only readout of a bench repeat set. Exists so the decomposition is a
 * number rather than an impression: "maybe a bit tighter" cannot distinguish
 * per-frame noise from a per-capture systematic, and those two want opposite
 * work done.
 *
 * Session-scoped — the log fills up as you press Repeat and resets when the
 * flow unmounts.
 */

const mm = (value: number | null | undefined, digits = 2) =>
  value == null ? '—' : value.toFixed(digits)

function Row({
  label,
  left,
  right,
  note,
}: {
  label: string
  left: string
  right: string
  note?: string
}) {
  return (
    <View className="flex-row items-baseline">
      <Typography variant="XS" className="w-40 text-black/50">
        {label}
      </Typography>
      <Typography variant="XS" className="w-20 text-right">
        {left}
      </Typography>
      <Typography variant="XS" className="w-20 text-right">
        {right}
      </Typography>
      {note ? (
        <Typography variant="XS" className="pl-4 text-black/40">
          {note}
        </Typography>
      ) : null}
    </View>
  )
}

const sideRows = (
  label: string,
  read: (side: ShCaptureLogSide) => number | null,
  left: ShCaptureLogSide,
  right: ShCaptureLogSide,
  note?: string
) => ({ label, left: mm(read(left)), right: mm(read(right)), note })

export function ShCaptureLogModal({
  open,
  entries,
  config,
  close,
  onClear,
}: {
  open: boolean
  entries: ShCaptureLogEntry[]
  config: ShCaptureLogConfig
  close: () => void
  onClear: () => void
}) {
  // Only this configuration's captures. Changing a settings row mid-session
  // makes every later capture a different measurement, so they are keyed rather
  // than pooled — and switching back resurfaces the earlier set, which is what
  // makes an A/B possible at all.
  const { active, otherCount } = selectShCaptureLogEntries(entries, config)
  const summary = summariseShCaptureLog(active)
  const [copiedAt, setCopiedAt] = useState(0)

  // The label reverts on its own; without this a stale "Copied" would still be
  // showing the next time the modal opens.
  useEffect(() => {
    if (copiedAt === 0) return
    const timer = setTimeout(() => setCopiedAt(0), 1500)
    return () => clearTimeout(timer)
  }, [copiedAt])

  // Always logs, even when the clipboard write succeeds: a debug readout that
  // silently loses the data because a permission prompt was declined is worse
  // than a duplicated console line.
  const report = () => {
    const text = formatShCaptureLogReport(entries, config)
    console.log(text)
    return text
  }

  return (
    <ModalWide
      open={open}
      close={close}
      title="Capture Log"
      showClose
      hideBottomSpace
      body={
        <View className="gap-4 px-16 pb-2">
          {/*
            First, always — a set means nothing without the configuration it ran
            under, and a chip left on an old preset silently outranks every
            shipped default.
          */}
          <View className="gap-0.5">
            {formatShCaptureLogConfig(config).map((line) => (
              <Typography
                key={line}
                variant="XS"
                className={
                  config.isModified ? 'text-amber-700' : 'text-black/50'
                }
              >
                {line}
              </Typography>
            ))}
            {otherCount > 0 ? (
              <Typography variant="XS" className="text-amber-700">
                {otherCount} further capture{otherCount === 1 ? '' : 's'} this
                session ran under other settings — not counted below.
              </Typography>
            ) : null}
          </View>

          {summary == null ? (
            <Typography variant="XS" className="text-black/50">
              No completed captures yet. Press Repeat to build a set — ten
              captures without touching the frame is the bench protocol.
            </Typography>
          ) : (
            <>
              <Typography variant="MDB">
                {summary.count} capture{summary.count === 1 ? '' : 's'}
              </Typography>

              <View className="gap-1">
                <Row label="" left="left" right="right" />
                {[
                  sideRows(
                    'reported mean',
                    (side) => side.meanMm,
                    summary.left,
                    summary.right,
                    'mm'
                  ),
                  sideRows(
                    'reported range',
                    (side) => side.rangeMm,
                    summary.left,
                    summary.right,
                    'mm'
                  ),
                  sideRows(
                    'between-capture SD',
                    (side) => side.sdMm,
                    summary.left,
                    summary.right,
                    'mm'
                  ),
                  sideRows(
                    'within-capture σ',
                    (side) => side.withinSigmaMm,
                    summary.left,
                    summary.right,
                    'mm/frame'
                  ),
                  sideRows(
                    'SD frame noise predicts',
                    (side) => side.predictedSdMm,
                    summary.left,
                    summary.right,
                    'mm'
                  ),
                  sideRows(
                    'ratio',
                    (side) => side.ratio,
                    summary.left,
                    summary.right,
                    'x'
                  ),
                ].map((row) => (
                  <Row key={row.label} {...row} />
                ))}
              </View>

              <Typography variant="XS" className="text-black/70">
                {readShCaptureLogRatio(summary.ratio)}
              </Typography>

              {/*
                Halves the search before any single correlation is read: SH
                scale and the pantoscopic-tilt factor are multipliers on both
                eyes, so they can only move the common mode. Per-eye detection
                bias moves the differential.
              */}
              <View className="gap-1">
                <Row
                  label="common-mode SD"
                  left={mm(summary.drivers.commonModeSdMm)}
                  right=""
                  note="mm — both eyes together"
                />
                <Row
                  label="differential SD"
                  left={mm(summary.drivers.differentialSdMm)}
                  right=""
                  note="mm — eyes apart"
                />
              </View>

              <View className="gap-1">
                <Typography variant="XS" className="text-black/50">
                  reported value vs each per-capture input — r / rho
                </Typography>
                {summary.drivers.drivers.map((driver) => (
                  <Row
                    key={driver.label}
                    label={driver.label}
                    left={formatShCaptureLogR(driver.r)}
                    right={formatShCaptureLogR(driver.spearman)}
                    note={[
                      driver.partial == null
                        ? null
                        : `partial ${formatShCaptureLogR(driver.partial)}`,
                      driver.isSignificant ? 'significant' : null,
                    ]
                      .filter((part) => part != null)
                      .join(' · ')}
                  />
                ))}
                {summary.drivers.controlledFor ? (
                  <Typography variant="XS" className="text-black/40">
                    partial holds “{summary.drivers.controlledFor}” fixed — a
                    column that collapses there was riding on it
                  </Typography>
                ) : null}
                {/*
                  rho is rank-based. Two extreme captures can manufacture a
                  Pearson r on a set this small, so a driver only counts when
                  both coefficients clear the bar.
                */}
                <Typography variant="XS" className="text-black/40">
                  both must clear |{criticalShCaptureLogR(summary.count)}| at n=
                  {summary.count}
                </Typography>
              </View>

              <View className="gap-1">
                <Typography variant="XS" className="text-black/50">
                  {`scale ${summary.scaleSources.join('/') || '—'}`}
                  {summary.switchedCount > 0
                    ? ` · ${summary.switchedCount} switched mid-capture`
                    : ''}
                  {summary.scaleCvMeanPct == null
                    ? ''
                    : ` · scaleCv ${summary.scaleCvMeanPct.toFixed(2)}%`}
                </Typography>
                <Typography variant="XS" className="text-black/50">
                  {`distance ${mm(summary.distanceMinCm, 1)}–${mm(
                    summary.distanceMaxCm,
                    1
                  )} cm · pitch ${mm(summary.pitchMinDeg, 1)}–${mm(
                    summary.pitchMaxDeg,
                    1
                  )}° · gaze ${mm(summary.gazeMinDeg, 1)}–${mm(
                    summary.gazeMaxDeg,
                    1
                  )}°`}
                </Typography>
              </View>

              <Typography variant="XS" className="text-black/40">
                Values are as first reported, before any manual adjustment.
              </Typography>
            </>
          )}
        </View>
      }
      buttons={
        // No Close button: `showClose` already puts one in the header, and a
        // fourth control here would crowd the row.
        <>
          <Button
            label="Clear"
            variant="outlined"
            disabled={entries.length === 0}
            onPress={onClear}
          />
          <Button
            label="Log"
            variant="outlined"
            disabled={entries.length === 0}
            onPress={() => report()}
          />
          <Button
            label={copiedAt === 0 ? 'Copy' : 'Copied'}
            disabled={entries.length === 0}
            onPress={() => {
              void copyText(report()).then(
                (ok) => ok && setCopiedAt(Date.now())
              )
            }}
          />
        </>
      }
    />
  )
}
